import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Tabs,
  Tab,
  TextField,
  FormControlLabel,
  Switch,
  Select,
  MenuItem,
  Button,
  useTheme,
  Stack,
  FormControl,
  InputLabel,
  SelectChangeEvent,
  Paper,
  Grid,
  Alert,
  Snackbar,
  CircularProgress,
  Checkbox,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { DataElement, DataElementType, dataElementsService } from '../../services/dataelements-service';
import { config } from '../../config/config';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { Language, languageService } from '../../services/language-service';
import { translationService } from '../../services/translation-service';
import AIIcon from '../../assets/images/Artificial_intelligence.png';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

interface EditDataElementPanelProps {
  open: boolean;
  onClose: () => void;
  selectedElement: DataElement | null;
  onSave?: () => void;
}

interface TranslationState {
  name: string;
  description: string;
  optionTranslations: Record<number, string>; // optionId -> translatedLabel
}

const EditDataElementPanel: React.FC<EditDataElementPanelProps> = ({ open, onClose, selectedElement, onSave }) => {
  const theme = useTheme();
  const [currentTab, setCurrentTab] = useState(0);
  const [editedElement, setEditedElement] = useState<Partial<DataElement>>({});
  const [elementTypes, setElementTypes] = useState<DataElementType[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [newOptionValue, setNewOptionValue] = useState('');
  const [newOptionLabel, setNewOptionLabel] = useState('');
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [optionLabelError, setOptionLabelError] = useState<string | null>(null);
  const [optionValueError, setOptionValueError] = useState<string | null>(null);
  const [languages, setLanguages] = useState<Language[]>([]);
  const [activeLanguage, setActiveLanguage] = useState<string>('en');
  const [translations, setTranslations] = useState<Record<string, TranslationState>>({});
  const [originalTranslations, setOriginalTranslations] = useState<Record<string, TranslationState>>({});
  const [isTranslating, setIsTranslating] = useState(false);
  const [isSavingTranslation, setIsSavingTranslation] = useState(false);
  const [needsGridRefresh, setNeedsGridRefresh] = useState(false);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const DRAWER_WIDTH = theme.customLayout.drawerWidth;
  const HEADER_HEIGHT = theme.customLayout.headerHeight;
  const MEDIUM_GAP = theme.customLayout.mediumGap;

  useEffect(() => {
    if (!open) {
      setCurrentTab(0);
      return;
    }

    const fetchData = async () => {
      try {
        setIsLoading(true);
        const types = await dataElementsService.getDataElementTypes();
        setElementTypes(types);
        if (selectedElement) {
          const elementWithOptions = {
            ...selectedElement,
            options: selectedElement.options || []
          };
          setEditedElement(elementWithOptions);
        }
      } catch (error) {
        setError('Failed to load data element types');
        setElementTypes([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [open, selectedElement]);

  useEffect(() => {
    let isMounted = true;
    if (!open || !selectedElement) return;
    
    setActiveLanguage(prev => {
      if (languages.length > 0) {
        return languages[0].code;
      }
      return 'en';
    });
    setIsTranslating(false);
    setIsSavingTranslation(false);

    languageService.getSupportedLanguages().then((langs) => {
      if (!isMounted) return;
      setLanguages(langs);

      if (langs.length > 0) {
        setActiveLanguage(langs[0].code);
      }

      dataElementsService.getDataElementTranslations(selectedElement.elementId).then((data) => {
        if (!isMounted) return;
        const translationsMap: Record<string, TranslationState> = {};

        // Build a lookup for fast access
        const translationLookup: Record<string, typeof data[0]> = {};
        data.forEach(t => {
          translationLookup[t.languageCode] = t;
        });

        langs.forEach(lang => {
          if (lang.code === 'en') {
            translationsMap['en'] = {
              name: selectedElement.elementName || '',
              description: selectedElement.description || '',
              optionTranslations: {}
            };
          } else if (translationLookup[lang.code]) {
            const t = translationLookup[lang.code];
            const optionTranslations: Record<number, string> = {};
            (t?.optionTranslations ?? []).forEach(ot => {
              optionTranslations[ot.optionId] = ot.translatedLabel;
            });
            translationsMap[lang.code] = {
              name: t.translatedName || '',
              description: t.translatedDescription || '',
              optionTranslations
            };
          } else {
            translationsMap[lang.code] = {
              name: '',
              description: '',
              optionTranslations: {}
            };
          }
        });
        setTranslations(translationsMap);
        setOriginalTranslations(translationsMap);
      });
    });

    return () => { isMounted = false; };
  }, [open, selectedElement]);

  const handleTextChange = (field: keyof DataElement) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setEditedElement(prev => ({
      ...prev,
      [field]: event.target.value,
    }));
    setHasChanges(true);
  };

  const handleSelectChange = (field: keyof DataElement) => (
    event: SelectChangeEvent
  ) => {
    setEditedElement(prev => ({
      ...prev,
      [field]: field === 'typeId' ? Number(event.target.value) : event.target.value,
    }));
    setHasChanges(true);
  };

  const handleSwitchChange = (field: keyof DataElement) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setEditedElement(prev => ({
      ...prev,
      [field]: event.target.checked,
    }));
    setHasChanges(true);
  };

  const validateOptionValue = (value: string) => {
    if (!/^[a-zA-Z0-9_]+$/.test(value.trim())) {
      return 'Option value can only contain alphanumeric characters and underscores (no spaces)';
    }
    return null;
  };

  const validateOptionLabel = (label: string) => {
    if (label.trim().length < 3) {
      return 'Option label must be at least 3 characters long';
    }
    return null;
  };

  const handleOptionAdd = () => {
    if (!newOptionValue.trim() || !newOptionLabel.trim()) {
      setError('Both option value and label are required');
      return;
    }
    const valueError = validateOptionValue(newOptionValue);
    const labelError = validateOptionLabel(newOptionLabel);
    
    if (valueError) {
      setOptionValueError(valueError);
      return;
    }
    if (labelError) {
      setOptionLabelError(labelError);
      return;
    }
    
    setOptionValueError(null);
    setOptionLabelError(null);
    setEditedElement(prev => ({
      ...prev,
      options: [
        ...(prev.options || []),
        {
          elementId: prev.elementId!,
          optionValue: newOptionValue.trim(),
          optionLabel: newOptionLabel.trim(),
          displayOrder: (prev.options?.length || 0) + 1,
          isEnabled: true,
        },
      ],
    }));
    setHasChanges(true);
    setNewOptionValue('');
    setNewOptionLabel('');
  };

  const handleOptionRemove = (index: number) => {
    setEditedElement(prev => ({
      ...prev,
      options: (prev.options || []).filter((_, i) => i !== index),
    }));
    setHasChanges(true);
  };

  const handleOptionChange = (index: number, field: 'optionValue' | 'optionLabel', value: string) => {
    setEditedElement(prev => ({
      ...prev,
      options: (prev.options || []).map((opt, i) => 
        i === index ? { ...opt, [field]: value } : opt
      ),
    }));
    setHasChanges(true);
  };

  const handleOptionMove = (index: number, direction: 'up' | 'down') => {
    const options = editedElement.options || [];
    if (!options.length) return;

    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= options.length) return;

    const newOptions = [...options];
    const temp = newOptions[index];
    newOptions[index] = newOptions[newIndex];
    newOptions[newIndex] = temp;

    // Update display order
    const updatedOptions = newOptions.map((opt, i) => ({
      ...opt,
      displayOrder: i + 1,
    }));

    setEditedElement(prev => ({
      ...prev,
      options: updatedOptions,
    }));
    setHasChanges(true);
  };

  const handleDragStart = (e: React.DragEvent, index: number) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    
    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      return;
    }

    const options = editedElement.options || [];
    const newOptions = [...options];
    const draggedItem = newOptions[draggedIndex];
    
    // Remove the dragged item
    newOptions.splice(draggedIndex, 1);
    
    // Insert at new position
    newOptions.splice(dropIndex, 0, draggedItem);

    // Update display order
    const updatedOptions = newOptions.map((opt, i) => ({
      ...opt,
      displayOrder: i + 1,
    }));

    setEditedElement(prev => ({
      ...prev,
      options: updatedOptions,
    }));
    
    setHasChanges(true);
    setDraggedIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  const handleSubmit = async () => {
    if (!selectedElement) return;
    setIsSaving(true);
    setError(null);

    try {
      await dataElementsService.updateDataElement(selectedElement.elementId, editedElement);
      setNeedsGridRefresh(true);
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to update data element');
    } finally {
      setIsSaving(false);
    }
  };

  const handleTranslationFieldChange = (field: 'name' | 'description', value: string) => {
    setTranslations(prev => ({
      ...prev,
      [activeLanguage]: {
        ...prev[activeLanguage],
        [field]: value,
      },
    }));
  };

  const handleOptionTranslationChange = (optionId: number, value: string) => {
    setTranslations(prev => ({
      ...prev,
      [activeLanguage]: {
        ...prev[activeLanguage],
        optionTranslations: {
          ...prev[activeLanguage].optionTranslations,
          [optionId]: value
        }
      },
    }));
  };

  const hasTranslationChanged = () => {
    const t = translations[activeLanguage] || { name: '', description: '', optionTranslations: {} };
    const o = originalTranslations[activeLanguage] || { name: '', description: '', optionTranslations: {} };
    
    // Check name and description
    if (t.name !== o.name || t.description !== o.description) return true;
    
    // Check option translations
    const tOptions = t.optionTranslations || {};
    const oOptions = o.optionTranslations || {};
    
    // Check if any option translations changed
    const allOptionIds = Array.from(new Set([...Object.keys(tOptions), ...Object.keys(oOptions)]))
      .map(id => parseInt(id, 10));
    
    for (const optionId of allOptionIds) {
      if (tOptions[optionId] !== oOptions[optionId]) return true;
    }
    
    return false;
  };

  const handleTranslateAI = async () => {
    if (!editedElement.elementName && !editedElement.description) return;
    
    setIsTranslating(true);
    try {
      const fields: { [key: string]: string } = {};
      if (editedElement.elementName) fields['name'] = editedElement.elementName;
      if (editedElement.description) fields['description'] = editedElement.description;
      if (editedElement.options) {
        editedElement.options.forEach(option => {
          if (option.optionLabel) {
            fields[`option_${option.optionId}`] = option.optionLabel;
          }
        });
      }

      const result = await translationService.translateBatch(fields, activeLanguage);
      
      const optionTranslations: Record<number, string> = {};
      if (editedElement.options) {
        editedElement.options.forEach(option => {
          const translated = result[`option_${option.optionId}`] || '';
          optionTranslations[option.optionId!] = translated;
        });
      }

      setTranslations(prev => ({
        ...prev,
        [activeLanguage]: {
          name: result['name'] || '',
          description: result['description'] || '',
          optionTranslations
        },
      }));
    } catch (e) {
      setError('AI translation failed');
    } finally {
      setIsTranslating(false);
    }
  };

  const handleSaveTranslation = async () => {
    if (!selectedElement) return;
    setIsSavingTranslation(true);
    try {
      const t = translations[activeLanguage];
      // Build array of NewOptionTranslation
      const optionTranslations = Object.entries(t.optionTranslations || {})
        .filter(([_, translatedLabel]) => translatedLabel && translatedLabel.trim().length > 0)
        .map(([optionId, translatedLabel]) => ({
          optionId: parseInt(optionId, 10),
          translatedLabel: translatedLabel.trim(),
        }));
      await dataElementsService.updateDataElementTranslation(
        selectedElement.elementId,
        activeLanguage,
        t.name,
        t.description,
        optionTranslations.length > 0 ? optionTranslations : undefined
      );
      
      // // Save option translations
      // if (editedElement.options && Object.keys(t.optionTranslations).length > 0) {
      //   await Promise.all(
      //     Object.entries(t.optionTranslations).map(([optionId, translatedLabel]) =>
      //       dataElementsService.updateDataElementOptionTranslation(
      //         selectedElement.elementId,
      //         parseInt(optionId),
      //         activeLanguage,
      //         translatedLabel
      //       )
      //     )
      //   );
      // }
      
      setOriginalTranslations(prev => ({
        ...prev,
        [activeLanguage]: { ...t },
      }));
    } catch (e) {
      setError('Failed to save translation');
    } finally {
      setIsSavingTranslation(false);
    }
  };

  const handlePanelClose = () => {
    if (hasChanges || hasTranslationChanged()) {
      setShowCloseConfirm(true);
    } else {
      if (needsGridRefresh) {
        onSave?.();
      }
      onClose();
      setNeedsGridRefresh(false);
    }
  };

  const handleConfirmClose = () => {
    setShowCloseConfirm(false);
    if (needsGridRefresh) {
      onSave?.();
    }
    onClose();
    setNeedsGridRefresh(false);
  };

  const handleCancelClose = () => {
    setShowCloseConfirm(false);
  };

  const handlePublishToggle = async () => {
    if (!selectedElement) return;

    const isCurrentlyPublished = editedElement.isPublished;
    const confirmMessage = isCurrentlyPublished
      ? 'Are you sure you want to unpublish this data element?\n\n' +
        'Unpublishing will make this data element unavailable to other parts of the program.\n' +
        'You can publish it again later when needed.'
      : 'Are you sure you want to publish this data element?\n\n' +
        'Publishing will make this data element available to other parts of the program to use.\n\n' +
        'Please ensure that you have:\n' +
        '• Reviewed all translations\n' +
        '• Verified the element name and description\n' +
        '• Selected the correct element type\n\n' +
        'This action cannot be undone.';

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      setIsPublishing(true);
      await dataElementsService.updatePublishStatus(selectedElement.elementId, !isCurrentlyPublished);
      setEditedElement(prev => ({ ...prev, isPublished: !isCurrentlyPublished }));
      setNeedsGridRefresh(true);
    } catch (error: any) {
      setError(error.response?.data?.message || `Failed to ${isCurrentlyPublished ? 'unpublish' : 'publish'} data element`);
    } finally {
      setIsPublishing(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedElement) return;
    setIsDeleting(true);
    setError(null);
    try {
      await dataElementsService.deleteDataElement(selectedElement.elementId);
      setIsDeleting(false);
      onSave?.();
      onClose();
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to delete data element');
      setIsDeleting(false);
    }
  };

  // Add a helper for read-only mode
  const isReadOnly = !!editedElement.isPublished;

  if (!selectedElement) return null;

  return (
    <Box
      sx={{
        bgcolor: theme.palette.background.paper,
        borderRadius: theme.shape.borderRadius,
        boxShadow: theme.shadows[24],
        p: 0,
        width: `calc(100vw - ${DRAWER_WIDTH + 2 * MEDIUM_GAP}px)`,
        height: `calc(100vh - ${HEADER_HEIGHT + 2 * MEDIUM_GAP}px)`,
        position: 'absolute',
        left: `${DRAWER_WIDTH + MEDIUM_GAP}px`,
        top: `${HEADER_HEIGHT + MEDIUM_GAP}px`,
        right: `${MEDIUM_GAP}px`,
        bottom: `${MEDIUM_GAP}px`,
        display: 'flex',
        flexDirection: 'column',
        outline: 'none',
      }}
    >
      <Box sx={{
        p: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        borderBottom: `1px solid ${theme.palette.divider}`,
      }}>
        <Typography variant="h6" sx={{ color: theme.palette.text.primary }}>
          Edit Data Element
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handlePublishToggle}
            disabled={isPublishing || isSaving || isSavingTranslation}
            sx={{
              bgcolor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
              '&:hover': {
                bgcolor: theme.palette.primary.dark,
              },
              '&.Mui-disabled': {
                bgcolor: theme.palette.action.disabledBackground,
                color: theme.palette.action.disabled,
              }
            }}
          >
            {isPublishing ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              editedElement.isPublished ? 'Unpublish Data Element' : 'Publish Data Element'
            )}
          </Button>
          <IconButton
            onClick={handlePanelClose}
            sx={{
              color: theme.palette.text.primary,
              '&:hover': {
                bgcolor: theme.palette.action.hover,
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>

      <Box sx={{ borderBottom: `1px solid ${theme.palette.divider}` }}>
        <Tabs
          value={currentTab}
          onChange={(_, v) => setCurrentTab(v)}
          sx={{
            '& .MuiTab-root': {
              color: theme.palette.text.secondary,
              '&.Mui-selected': {
                color: theme.palette.primary.main,
              },
            },
          }}
        >
          <Tab 
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                Details
                {hasChanges && (
                  <Typography 
                    component="span" 
                    sx={{ 
                      color: theme.palette.primary.main,
                      opacity: 0.8,
                      fontSize: '1.2rem',
                      lineHeight: 1,
                      ml: 0.5,
                      fontWeight: 500
                    }}
                  >
                    •
                  </Typography>
                )}
              </Box>
            } 
          />
          <Tab 
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                Translations
                {hasTranslationChanged() && (
                  <Typography 
                    component="span" 
                    sx={{ 
                      color: theme.palette.primary.main,
                      opacity: 0.8,
                      fontSize: '1.2rem',
                      lineHeight: 1,
                      ml: 0.5,
                      fontWeight: 500
                    }}
                  >
                    •
                  </Typography>
                )}
              </Box>
            } 
          />
        </Tabs>
      </Box>

      <Box sx={{ flex: 1, overflow: 'auto', bgcolor: theme.palette.background.default }}>
        {currentTab === 0 && (
          <Box sx={{ p: 3 }}>
            <Grid container spacing={3}>
              {/* Left Column - Basic Info */}
              <Grid item xs={6}>
                <Stack spacing={3}>
                  <TextField
                    label="Element Name"
                    value={editedElement.elementName || ''}
                    onChange={handleTextChange('elementName')}
                    fullWidth
                    required
                    size="small"
                    disabled={isReadOnly}
                  />

                  <TextField
                    label="Element Code"
                    value={editedElement.elementCode || ''}
                    fullWidth
                    disabled
                    size="small"
                  />

                  <TextField
                    label="Description"
                    value={editedElement.description || ''}
                    onChange={handleTextChange('description')}
                    fullWidth
                    multiline
                    rows={4}
                    size="small"
                    disabled={isReadOnly}
                  />

                  <FormControl fullWidth size="small" disabled={isReadOnly}>
                    <InputLabel>Type</InputLabel>
                    <Select
                      value={editedElement.typeId?.toString() || ''}
                      onChange={handleSelectChange('typeId')}
                      label="Type"
                      disabled={isReadOnly}
                    >
                      {elementTypes.map((type) => (
                        <MenuItem key={type.TypeId} value={type.TypeId}>
                          {type.TypeName}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControlLabel
                    control={
                      <Switch
                        checked={editedElement.isIdentifier || false}
                        onChange={handleSwitchChange('isIdentifier')}
                        disabled={isReadOnly}
                      />
                    }
                    label="Is Identifier"
                    sx={{ '& .MuiFormControlLabel-label': { color: theme.palette.text.primary } }}
                  />

                  <FormControlLabel
                    control={
                      <Switch
                        checked={editedElement.isPersonalData || false}
                        onChange={handleSwitchChange('isPersonalData')}
                        disabled={isReadOnly}
                      />
                    }
                    label="Is Personal Data"
                    sx={{ '& .MuiFormControlLabel-label': { color: theme.palette.text.primary } }}
                  />

                  <TextField
                    label="Validation Rule"
                    value={editedElement.validationRule || ''}
                    onChange={handleTextChange('validationRule')}
                    fullWidth
                    size="small"
                    disabled={isReadOnly}
                  />

                  {/* Danger Zone */}
                  {editedElement.isActive !== false && (
                    <Box sx={{
                      mt: 4,
                      p: 3,
                      border: `1px solid ${theme.palette.error.main}`,
                      borderRadius: theme.shape.borderRadius,
                      bgcolor: theme.palette.mode === 'dark' ? 'rgba(239, 83, 80, 0.08)' : 'rgba(211, 47, 47, 0.04)',
                    }}>
                      <Typography variant="h6" sx={{ color: theme.palette.error.main, mb: 1 }}>
                        Danger Zone
                      </Typography>
                      <Typography variant="body2" sx={{ color: theme.palette.error.main, mb: 2 }}>
                        Data element once deleted cannot be retrieved
                      </Typography>
                      <Button
                        variant="contained"
                        color="error"
                        onClick={() => setShowDeleteConfirm(true)}
                        disabled={isDeleting || isReadOnly}
                        sx={{
                          bgcolor: theme.palette.error.main,
                          '&:hover': {
                            bgcolor: theme.palette.error.dark,
                          },
                        }}
                      >
                        {isDeleting ? <CircularProgress size={24} color="inherit" /> : 'Delete Data Element'}
                      </Button>
                    </Box>
                  )}
                </Stack>
              </Grid>

              {/* Right Column - Options */}
              <Grid item xs={6}>
                <Paper 
                  sx={{ 
                    p: 2, 
                    bgcolor: theme.palette.background.paper,
                    border: `1px solid ${theme.palette.divider}`,
                  }}
                >
                  <Typography variant="h6" sx={{ mb: 2, color: theme.palette.text.primary }}>
                    Options
                  </Typography>

                  {/* Always allow options for any type */}
                  <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                    <TextField
                      label="Option Value"
                      value={newOptionValue}
                      onChange={(e) => {
                        setNewOptionValue(e.target.value);
                        setOptionValueError(validateOptionValue(e.target.value));
                      }}
                      size="small"
                      sx={{ flex: 1 }}
                      error={!!optionValueError}
                      helperText={optionValueError}
                      disabled={isReadOnly}
                    />
                    <TextField
                      label="Option Label"
                      value={newOptionLabel}
                      onChange={(e) => {
                        setNewOptionLabel(e.target.value);
                        setOptionLabelError(validateOptionLabel(e.target.value));
                      }}
                      size="small"
                      sx={{ flex: 1 }}
                      error={!!optionLabelError}
                      helperText={optionLabelError}
                      disabled={isReadOnly}
                    />
                    <Button
                      variant="contained"
                      onClick={handleOptionAdd}
                      sx={{ bgcolor: '#e6ff60', color: '#222', minWidth: 80 }}
                      disabled={isReadOnly || !newOptionValue.trim() || !newOptionLabel.trim() || !!optionValueError || !!optionLabelError}
                    >
                      + Add
                    </Button>
                  </Box>

                  {/* Options List */}
                  <Box sx={{ maxHeight: 'calc(100vh - 400px)', overflowY: 'auto' }}>
                    {(editedElement.options || []).map((option, index) => (
                      <Paper
                        key={index}
                        draggable={!isReadOnly}
                        onDragStart={isReadOnly ? undefined : (e) => handleDragStart(e, index)}
                        onDragOver={isReadOnly ? undefined : handleDragOver}
                        onDragEnter={isReadOnly ? undefined : handleDragEnter}
                        onDrop={isReadOnly ? undefined : (e) => handleDrop(e, index)}
                        onDragEnd={isReadOnly ? undefined : handleDragEnd}
                        sx={{
                          p: 2,
                          mb: 1.5,
                          display: 'flex',
                          alignItems: 'center',
                          bgcolor: draggedIndex === index ? '#2a2a2a' : '#232323',
                          border: 'none',
                          boxShadow: 'none',
                          opacity: draggedIndex === index ? 0.5 : 1,
                          cursor: isReadOnly ? 'not-allowed' : 'move',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            bgcolor: '#2a2a2a',
                          },
                        }}
                      >
                        {/* Drag handle */}
                        <Box sx={{ 
                          pr: 2, 
                          color: '#888', 
                          cursor: 'grab', 
                          fontSize: 22, 
                          display: 'flex', 
                          alignItems: 'center',
                          '&:active': {
                            cursor: 'grabbing'
                          }
                        }}>
                          <span style={{ fontSize: 22, lineHeight: 1 }}>⋮⋮</span>
                        </Box>
                        {/* Option label and value */}
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="subtitle1" sx={{ color: '#fff', fontWeight: 500 }}>
                            {option.optionLabel || <span style={{ color: '#aaa' }}>(No label)</span>}
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#aaa' }}>
                            Value: {option.optionValue || <span style={{ color: '#555' }}>(No value)</span>}
                          </Typography>
                        </Box>
                        {/* Delete icon */}
                        <IconButton
                          onClick={() => handleOptionRemove(index)}
                          size="small"
                          sx={{ color: '#ff5252' }}
                          disabled={isReadOnly}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Paper>
                    ))}
                  </Box>
                </Paper>
                {/* Save button at bottom right */}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSubmit}
                    disabled={isReadOnly || !hasChanges || isSaving}
                    sx={{
                      bgcolor: '#e6ff60',
                      color: '#222',
                      fontWeight: 600,
                      minWidth: 120,
                      '&:hover': {
                        bgcolor: '#d4f24a',
                      },
                    }}
                  >
                    {isSaving ? <CircularProgress size={24} color="inherit" /> : 'Save'}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Box>
        )}

        {currentTab === 1 && (
          <Box sx={{ p: 3, display: 'flex', gap: 3, height: `calc(100% - ${MEDIUM_GAP}px)` }}>
            {/* Language list */}
            <Box sx={{ minWidth: 300, maxHeight: '100%', overflowY: 'auto', border: `1px solid ${theme.palette.divider}`, borderRadius: theme.shape.borderRadius, p: 0, bgcolor: theme.palette.background.paper }}>
              {languages.map(lang => {
                const isSelected = activeLanguage === lang.code;
                const changed = (translations[lang.code]?.name !== originalTranslations[lang.code]?.name) || (translations[lang.code]?.description !== originalTranslations[lang.code]?.description);
                const accepted = !!(translations[lang.code]?.name || translations[lang.code]?.description);
                const isNotSupported = lang.translationProvider === null;
                
                const rowContent = (
                  <Box 
                    key={lang.code} 
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      px: 2, 
                      py: 1, 
                      borderBottom: `1px solid ${theme.palette.divider}`, 
                      bgcolor: isSelected ? theme.palette.action.selected : 'inherit', 
                      cursor: isNotSupported ? 'default' : 'pointer', 
                      opacity: isNotSupported ? 0.5 : 1,
                      '&:hover': { 
                        bgcolor: isNotSupported ? undefined : theme.palette.action.hover 
                      } 
                    }} 
                    onClick={() => !isNotSupported && setActiveLanguage(lang.code)}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.text.primary, flex: 1 }}>{lang.name}</Typography>
                    <Typography variant="body2" sx={{ color: theme.palette.text.secondary, flex: 1 }}>{lang.localeName}</Typography>
                    <Checkbox
                      checked={accepted}
                      readOnly
                      size="small"
                      sx={{ p: 0, ml: 1 }}
                    />
                    {lang.code !== 'en' && changed && <Typography component="span" sx={{ color: theme.palette.primary.main, fontSize: '1.2rem', fontWeight: 600, ml: 1 }}>•</Typography>}
                  </Box>
                );

                return isNotSupported ? (
                  <Tooltip 
                    key={lang.code}
                    title="This language is not supported by our translation service. Translations for this language cannot be provided."
                    arrow
                  >
                    <span style={{ display: 'block' }}>{rowContent}</span>
                  </Tooltip>
                ) : rowContent;
              })}
            </Box>
            {/* Translation form */}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle1" sx={{ color: theme.palette.text.primary }}>
                  Language: {languages.find((l) => l.code === activeLanguage)?.name || activeLanguage}
                </Typography>
                {activeLanguage !== 'en' && (
                  <>
                    <Button
                      variant="outlined"
                      size="small"
                      sx={{
                        ml: 2,
                        fontWeight: 600,
                        color: theme.palette.primary.main,
                        borderColor: theme.palette.primary.main,
                        '&:hover': {
                          borderColor: theme.palette.primary.dark,
                          color: theme.palette.primary.dark,
                        },
                      }}
                      disabled={isTranslating || isReadOnly}
                      onClick={handleTranslateAI}
                      endIcon={<img src={AIIcon} alt="AI" style={{ width: 20, height: 20 }} />}
                    >
                      {isTranslating ? 'Translating...' : 'Translate using AI'}
                    </Button>
                    <Tooltip
                      title="AI-generated translations may contain errors or inaccuracies. Please review and cross-check the translation before accepting."
                      arrow
                    >
                      <InfoOutlinedIcon 
                        fontSize="small" 
                        sx={{ 
                          ml: 1, 
                          color: theme.palette.neutral ? theme.palette.neutral.main : theme.palette.text.secondary, 
                          cursor: 'pointer',
                          fontWeight: 400
                        }} 
                      />
                    </Tooltip>
                  </>
                )}
              </Box>
              <TextField
                label={selectedElement.elementName || "Element Name"}
                value={translations[activeLanguage]?.name || ''}
                onChange={e => handleTranslationFieldChange('name', e.target.value)}
                disabled={activeLanguage === 'en' || isReadOnly}
                fullWidth
                size="small"
                sx={{ mb: 2 }}
              />
              <TextField
                label={selectedElement.description || "Description"}
                value={translations[activeLanguage]?.description || ''}
                onChange={e => handleTranslationFieldChange('description', e.target.value)}
                disabled={activeLanguage === 'en' || isReadOnly}
                fullWidth
                multiline
                rows={3}
              />
              {activeLanguage !== 'en' && (
                <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                  <Button variant="contained" onClick={handleSaveTranslation} disabled={isReadOnly || isSavingTranslation || !hasTranslationChanged()} sx={{ fontWeight: 600, bgcolor: theme.palette.primary.main, color: theme.palette.primary.contrastText, '&:hover': { bgcolor: theme.palette.primary.dark } }}>{isSavingTranslation ? <CircularProgress size={24} color="inherit" /> : 'Save'}</Button>
                </Box>
              )}
              {/* Option translations */}
              {editedElement.options && editedElement.options.length > 0 && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle1" sx={{ mb: 2, color: theme.palette.text.primary }}>
                    Option Translations
                  </Typography>
                  <Stack spacing={2}>
                    {editedElement.options.map((option) => (
                      <TextField
                        key={option.optionId}
                        label={`${option.optionLabel} (${option.optionValue})`}
                        value={translations[activeLanguage]?.optionTranslations?.[option.optionId!] || ''}
                        onChange={(e) => handleOptionTranslationChange(option.optionId!, e.target.value)}
                        disabled={activeLanguage === 'en' || isReadOnly}
                        fullWidth
                        size="small"
                      />
                    ))}
                  </Stack>
                </Box>
              )}
            </Box>
          </Box>
        )}
      </Box>

      {/* Add Confirmation Dialog */}
      <Dialog
        open={showCloseConfirm}
        onClose={handleCancelClose}
        aria-labelledby="close-confirm-dialog-title"
      >
        <DialogTitle id="close-confirm-dialog-title">
          Unsaved Changes
        </DialogTitle>
        <DialogContent>
          <Typography>
            You have unsaved changes. Are you sure you want to close without saving?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelClose} color="primary">
            Cancel
          </Button>
          <Button onClick={handleConfirmClose} color="error" autoFocus>
            Close Without Saving
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        aria-labelledby="delete-confirm-dialog-title"
      >
        <DialogTitle id="delete-confirm-dialog-title">
          Delete Data Element
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this data element? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDeleteConfirm(false)} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDelete} color="error" autoFocus disabled={isDeleting}>
            {isDeleting ? <CircularProgress size={24} color="inherit" /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar 
        open={!!error} 
        autoHideDuration={config.ui.snackbar.autoHideDuration}
        onClose={() => setError(null)}
        anchorOrigin={config.ui.snackbar.anchorOrigin}
      >
        <Alert onClose={() => setError(null)} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default EditDataElementPanel;
